import React, { useState, useEffect, useMemo } from 'react';
import styles from './Dashboard.module.css';

// Importando os componentes das seções
import FaturamentoSection from './FaturamentoSection';
import CompromissosSection from './CompromissosSection';
import ServicosSection from './ServicosSection';
import ClientesSection from './ClientesSection';

// Importando o hook principal de dados
import { useDataManagement } from '../ferramentas/RequisitarAPI.ts';

// --- Helpers e Componentes Internos ---

// Uma função helper para formatação de moeda
const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
    }).format(value);
};

// Uma função para obter o intervalo de datas inicial (ex: mês atual)
const getInitialDateRange = () => {
    const hoje = new Date();
    const primeiroDia = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
    return {
        inicio: primeiroDia.toISOString().split('T')[0],
        fim: hoje.toISOString().split('T')[0],
    };
};

// Componente para exibir enquanto a permissão é verificada
const CheckingAccess = () => (
    <div className={styles.centeredFeedback}>
        <h2>Verificando Acesso...</h2>
        <p>Aguarde um momento.</p>
    </div>
);

// Componente para exibir quando o acesso é negado
const AccessDenied = () => (
    <div className={styles.centeredFeedback}>
        <h2>Acesso Negado</h2>
        <p>Você não tem permissão para visualizar estes relatórios. Por favor, contate um administrador.</p>
    </div>
);


// --- Componente Principal do Dashboard ---

const Dashboard = () => {
    // Definindo as abas de forma declarativa para facilitar a manutenção
    const tabs = useMemo(() => [
        { id: 'faturamento', label: 'Faturamento', Component: FaturamentoSection },
        { id: 'compromissos', label: 'Compromissos', Component: CompromissosSection },
        { id: 'servicos', label: 'Serviços', Component: ServicosSection },
        { id: 'clientes', label: 'Clientes', Component: ClientesSection },
    ], []);

    // Estado para controlar a aba ativa e o status de acesso
    const [activeTab, setActiveTab] = useState(tabs[0].id);
    const [accessStatus, setAccessStatus] = useState('checking'); // 'checking', 'granted', 'denied'
    
    // Hook para fazer a verificação de acesso
    const { reports } = useDataManagement();

    // Efeito para verificar a permissão de acesso ao montar o componente
    useEffect(() => {
        const checkAccess = async () => {
            try {
                // Uma chamada leve apenas para verificar se o usuário tem acesso ao módulo
                const response = await reports.revenue({}); 
                
                if (response.ok) {
                    setAccessStatus('granted');
                } else if (response.error === 'mod_access_required') {
                    setAccessStatus('denied');
                } else {
                    // Trata outros erros como acesso negado por padrão
                    console.error("Erro inesperado ao verificar acesso:", response.error);
                    setAccessStatus('denied');
                }
            } catch (err) {
                console.error("Falha na requisição de verificação de acesso:", err);
                setAccessStatus('denied');
            }
        };

        checkAccess();
    }, [reports]); // Depende do `reports` do hook

    // Memoiza as props comuns e o range de data para evitar recálculos
    const initialDateRange = useMemo(() => getInitialDateRange(), []);
    const commonProps = useMemo(() => ({
        styles,
        formatCurrency,
        initialDateRange,
    }), [initialDateRange]);

    // Renderização condicional com base no status de acesso
    if (accessStatus === 'checking') {
        return <CheckingAccess />;
    }

    if (accessStatus === 'denied') {
        return <AccessDenied />;
    }

    // Encontra o componente da aba ativa para renderização
    const ActiveComponent = tabs.find(tab => tab.id === activeTab)?.Component;

    return (
        <div className={styles.container}>
            <header className={styles.mainHeader}>
                <h1 className={styles.mainTitle}>Dashboard de Relatórios</h1>
                <a href="/admin/calendario" className={styles.backButton}>
                    Voltar ao Calendário
                </a>
            </header>

            <nav className={styles.tabsNav}>
                <ul className={styles.tabsList}>
                    {tabs.map(tab => (
                        <li key={tab.id}>
                            <button
                                className={`${styles.tabButton} ${activeTab === tab.id ? styles.activeTab : ''}`}
                                onClick={() => setActiveTab(tab.id)}
                            >
                                {tab.label}
                            </button>
                        </li>
                    ))}
                </ul>
            </nav>

            <main>
                {/* Renderiza o componente da aba ativa, se encontrado */}
                {ActiveComponent && <ActiveComponent {...commonProps} />}
            </main>
        </div>
    );
};

export default Dashboard;